import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useTranslate } from '@tolgee/react'
import { PATHS } from '@tom-ui/utils'
import { BreadcrumpItem } from 'modules/ui/src/theme/components/breadcrumbs/Breadcrumb'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

export const useVesselVisitBreadcrumbs = (vesselVisit?: IVesselVisitItem) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const { pathname } = useLocation() as {
    pathname: string
  }

  const path = pathname.split('/').filter(Boolean)
  const normalize = (value: string) => {
    while (value.startsWith('/')) {
      value = value.slice(1)
    }
    while (value.endsWith('/')) {
      value = value.slice(0, -1)
    }
    return value
  }

  const isVesselVisitPlanningRoute = path.includes(normalize(PATHS.visits.vesselPlanning.url))
  const isVesselVisitDischargeRoute = path.includes(PATHS.visits.vesselVisits.discharge.url)
  const isVesselVisitLoadRoute = path.includes(PATHS.visits.vesselVisits.load.url)
  const isVesselVisitRestowRoute = path.includes(PATHS.visits.vesselVisits.restow.url)

  const isNnrOrdersRoute = path.includes('nnr-orders')
  const isAssignContainerRoute = path.includes('assign-container')

  const isEdit = Boolean(vesselVisit?.id)

  const carrierVisitLabel = useMemo(() => {
    const vessel = vesselVisit?.vessels?.[0]?.data

    if (!vessel) return

    return `${vessel.name}:${vesselVisit.data.number}`
  }, [vesselVisit?.data.number, vesselVisit?.vessels])

  const directionLabel = useMemo(() => {
    if (isVesselVisitDischargeRoute) return t('dischargeList', 'Discharge List')
    if (isVesselVisitLoadRoute) return t('loadList', 'Load List')
    if (isVesselVisitRestowRoute) return t('restowList', 'Restow List')
  }, [isVesselVisitDischargeRoute, isVesselVisitLoadRoute, isVesselVisitRestowRoute, t])

  const breadcrumbs = useMemo(() => {
    const basePath = PATHS.visits.vesselVisits.url
    const baseLabel = t('vesselVisits', 'Vessel visits')

    const carrierVisitDashboardUrl = `${basePath}/${vesselVisit?.id}/dashboard`

    const breadcrumbs: BreadcrumpItem[] = [
      {
        label: baseLabel,
        onClick: () => navigate(basePath),
      },
    ]

    if (carrierVisitLabel) {
      breadcrumbs.push({
        label: carrierVisitLabel,
        onClick: () =>
          (isVesselVisitPlanningRoute && isEdit) ||
          isVesselVisitDischargeRoute ||
          isVesselVisitLoadRoute ||
          isVesselVisitRestowRoute
            ? navigate(carrierVisitDashboardUrl)
            : undefined,
      })
    }

    if (isVesselVisitPlanningRoute) {
      breadcrumbs.push({
        label: isEdit
          ? t('editVesselVisit', 'Edit Vessel Visit')
          : t('createVesselVisit', 'Create Vessel Visit'),
      })
    } else if (directionLabel) {
      const isNestedDirectionRoute = isNnrOrdersRoute || isAssignContainerRoute

      breadcrumbs.push({
        label: directionLabel,
        onClick: isNestedDirectionRoute
          ? () => {
              const directionPath = pathname.split('/nnr-orders')[0]
              navigate(directionPath)
            }
          : undefined,
      })

      if (isNnrOrdersRoute) {
        breadcrumbs.push({
          label: t('nonNumericReleaseDetails', 'Non-numeric release details'),
          onClick: isAssignContainerRoute
            ? () => {
                navigate(pathname)
              }
            : undefined,
        })
      }

      if (isAssignContainerRoute) {
        breadcrumbs.push({
          label: t('assignContainer', 'Assign Container'),
        })
      }
    }

    return breadcrumbs
  }, [
    carrierVisitLabel,
    directionLabel,
    isEdit,
    isVesselVisitDischargeRoute,
    isVesselVisitLoadRoute,
    isVesselVisitPlanningRoute,
    isVesselVisitRestowRoute,
    isNnrOrdersRoute,
    isAssignContainerRoute,
    navigate,
    pathname,
    t,
    vesselVisit?.id,
  ])

  return { breadcrumbs }
}
